import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AvailableOptionsResponseModel,
  CalculatePriceModel,
  CheckPendingServiceRequestModel,
  LocationModel,
  PostGetAvailableOptionsModel,
  PostGetSlotsModel,
  PostServiceRequestModel,
  PricingModel,
  ServiceModel,
  ServiceRequestModel,
  SlotModel,
} from '@app/core/models';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { BookingRepository, ServiceRequestsRepository } from '@app/store';
import { ServicesRepository } from '@app/store/services.repository';
import { TranslocoService } from '@jsverse/transloco';
import { trackRequestResult } from '@ngneat/elf-requests';
import { format } from 'date-fns';
import { Observable, from, map, of, switchMap, tap } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { environment } from 'src/environments/environment';
import { FirebaseAuthenticationService } from '../firebase/firebase-authentication/firebase-authentication.service';
import { ServiceRequestService } from './service-request.service';

interface SearchLocationsParams {
  serviceId: string;
  lat: number;
  lng: number;
  dateFrom: string;
  dateTo: string;
  quantity: number;
}

/**
 * Service responsible for handling authentication-related functionality.
 */
@Injectable()
export class ServiceService {
  constructor(
    private _router: Router,
    private _http: HttpClient,
    private _servicesRepo: ServicesRepository,
    private _serviceRequestsRepo: ServiceRequestsRepository,
    private _translocoService: TranslocoService,
    private authService: FirebaseAuthenticationService,
    private _bookingRepo: BookingRepository,
    private _serviceRequestService: ServiceRequestService,
  ) {}

  /**
   * Retrieves the services associated with a property.
   * @param propertyId - The ID of the property to retrieve services for.
   * @returns An Observable that emits an array of services.
   */
  getAllByProperty(
    propertyId: string,
    bookingId: string,
  ): Observable<ServiceModel[]> {
    const currentLang = this._translocoService.getActiveLang();
    return fromFetch<ServiceModel[]>(
      `${environment.api.serverUrl}/properties/${propertyId}/services?bookingId=${bookingId}`,
      {
        selector: (response) => response.json(),
        headers: {
          'Accept-Language': currentLang,
          'Content-Type': 'application/json',
        },
      },
    ).pipe(
      tap((services) => {
        this._servicesRepo.setServices(services);
      }),
      trackRequestResult(['services'], { skipCache: true }),
    );
  }

  getAll(locationId: string): Observable<ServiceModel[]> {
    const currentLang = this._translocoService.getActiveLang();
    let queryParams = `locationId=${locationId}`;

    LoggingService.log('booking', this._bookingRepo.activeBooking);

    if (this._bookingRepo.activeBooking) {
      queryParams += `&bookingId=${this._bookingRepo.activeBooking._id}`;
    }

    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        // Use the Headers constructor correctly and include the token
        const headers = new Headers({
          Authorization: `Bearer ${data.token}`,
          'Accept-Language': currentLang,
          'Content-Type': 'application/json',
        });

        return fromFetch<ServiceModel[]>(
          `${environment.api.serverUrl}/services?${queryParams}`,
          {
            selector: (response) => response.json(),
            headers: headers,
          },
        );
      }),
      tap((services) => {
        if (services && services.length > 0) {
          this._servicesRepo.setServices(services);
        } else {
          this._servicesRepo.clearServicesAndCategories();
        }
      }),
      trackRequestResult(['services'], { skipCache: true }),
    );
  }

  calculatePrice(data: CalculatePriceModel): Observable<PricingModel> {
    const params = {
      ...data,
      addOns: data.addOns?.map((addOn) => ({
        _id: addOn._id,
        quantity: addOn.quantity,
      })),
    };
    return this._http.post<PricingModel>(
      `${environment.api.serverUrl}/services/${data.service}/calculate-price`,
      { ...params },
    );
  }

  /**
   * Retrieves the service slots from the server.
   * @returns An observable that emits the service slots.
   */
  getAvailableSlots(filters: PostGetSlotsModel): Observable<SlotModel[]> {
    let params = new HttpParams();

    params = params.set(
      'dateFrom',
      format(new Date(filters.dateFrom), 'yyyy-MM-dd'),
    );
    params = params.set(
      'dateTo',
      filters.dateTo
        ? format(new Date(filters.dateTo), 'yyyy-MM-dd')
        : format(new Date(filters.dateFrom), 'yyyy-MM-dd'),
    );
    params = params.set('quantity', filters.quantity);
    params = params.set('includeInfo', filters.includeInfo);
    params = params.set(
      'pricingOptions',
      JSON.stringify(filters.pricingOptions),
    );
    if (filters.voucher) {
      params = params.set('voucher', filters.voucher);
    }
    if (filters.apartmentId) {
      params = params.set('apartmentId', filters.apartmentId);
    }
    if (filters.bookingId) {
      params = params.set('bookingId', filters.bookingId);
    }
    try {
      return this._http.get<SlotModel[]>(
        `${environment.api.serverUrl}/services/${filters.service?._id}/slots`,
        { params },
      );
    } catch {
      return of([]);
    }
  }

  /**
   * Availability-first flow: given an exact slot the guest picked (date + time),
   * resolves which pricingOptions/quantity are available for it. Mirrors
   * `getAvailableSlots` but sends full ISO datetimes (the slot is the input).
   */
  getAvailableOptions(
    filters: PostGetAvailableOptionsModel,
  ): Observable<AvailableOptionsResponseModel> {
    const body: Record<string, any> = {
      dateFrom: new Date(filters.dateFrom).toISOString(),
      dateTo: filters.dateTo
        ? new Date(filters.dateTo).toISOString()
        : new Date(filters.dateFrom).toISOString(),
      includeInfo: true,
    };
    if (filters.voucher) {
      body['voucher'] = filters.voucher;
    }
    if (filters.apartmentId) {
      body['apartmentId'] = filters.apartmentId;
    }
    if (filters.bookingId) {
      body['bookingId'] = filters.bookingId;
    }
    if (filters.context) {
      body['context'] = filters.context;
    }

    return this._http.post<AvailableOptionsResponseModel>(
      `${environment.api.serverUrl}/services/${filters.service?._id}/available-options`,
      body,
    );
  }

  checkPendingServiceRequest(
    serviceId: string,
    body: CheckPendingServiceRequestModel,
  ): Observable<ServiceRequestModel | null> {
    return this._http.post<ServiceRequestModel | null>(
      `${environment.api.serverUrl}/services/${serviceId}/service-requests/check-pending`,
      body,
    );
  }

  createServiceRequest(
    serviceId: string,
    serviceRequest: PostServiceRequestModel,
  ): Observable<ServiceRequestModel> {
    return this._http
      .post<ServiceRequestModel>(
        `${environment.api.serverUrl}/services/${serviceId}/service-request`,
        { ...serviceRequest },
      )
      .pipe(
        tap((serviceRequest) => {
          this._serviceRequestsRepo.createServiceRequests(serviceRequest);
        }),
      );
  }

  cancelServiceRequest(
    serviceRequest: ServiceRequestModel,
  ): Observable<ServiceRequestModel> {
    return this._http
      .patch<ServiceRequestModel>(
        `${environment.api.serverUrl}/services/${serviceRequest.service._id}/service-request/${serviceRequest._id}/cancel`,
        {},
      )
      .pipe(
        switchMap((patchResponse) =>
          this._serviceRequestService.getAll().pipe(map(() => patchResponse)),
        ),
      );
  }

  searchLocations(params: SearchLocationsParams): Observable<LocationModel[]> {
    let httpParams = new HttpParams()
      .set('lat', params.lat.toString())
      .set('lng', params.lng.toString())
      .set('dateFrom', params.dateFrom)
      .set('dateTo', params.dateTo)
      .set('quantity', params.quantity.toString());

    return this._http.get<LocationModel[]>(
      `${environment.api.serverUrl}/services/${params.serviceId}/locations`,
      { params: httpParams },
    );
  }

  requestPayment(serviceRequest: any): Observable<ServiceRequestModel> {
    return this._http.patch<ServiceRequestModel>(
      `${environment.api.serverUrl}/services/${serviceRequest.service._id}/service-request/${serviceRequest._id}/request-payment`,
      {
        paymentMethod: 'stripe',
      },
    );
  }
}
